import type { WppTicketMessage } from '../types/whatsappTickets'

/** Escala CSAT Blip PASI Farma. */
export const FARMA_RATING_SCALE = [1, 2, 3] as const
/** Escala CSAT Blip bot principal (legado). */
export const WPP_RATING_SCALE = [1, 2, 3, 4, 5] as const

export function ratingScaleForOrigin(origin: string | undefined): readonly number[] {
  // WhatsApp Blip (principal e Farma): CSAT 1–3
  if (origin === 'pasi-farma' || origin === 'bot-principal') return FARMA_RATING_SCALE
  return WPP_RATING_SCALE
}

function normalizeMessageText(text: string): string {
  return text
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&colon;/gi, ':')
    .replace(/：/g, ':')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
}

/**
 * Extrai a nota CSAT da Blip a partir do texto das mensagens.
 * Farma: 1–3. Ex.: "Pronto, salvei aqui a avaliação seu atendimento: … Nota: 3"
 */
export function extractBlipRatingFromText(
  text: string | null | undefined,
  maxRating = 5,
): number | null {
  if (!text) return null
  const plain = normalizeMessageText(text)
  const patterns = [
    new RegExp(`Nota\\s*:\\s*([1-${maxRating}])\\b`, 'i'),
    new RegExp(`Nota\\s+([1-${maxRating}])\\b`, 'i'),
    new RegExp(`avalia[cç][aã]o[^0-9]{0,40}?([1-${maxRating}])\\b`, 'i'),
  ]
  for (const re of patterns) {
    const match = plain.match(re)
    if (!match) continue
    const n = Number(match[1])
    if (Number.isFinite(n) && n >= 1 && n <= maxRating) return n
  }
  return null
}

/** Percorre mensagens (mais recentes primeiro) e devolve a primeira nota Blip válida. */
export function extractBlipRatingFromMessages(
  messages: Array<Pick<WppTicketMessage, 'message' | 'datetime'>>,
  maxRating = 5,
): number | null {
  const ordered = [...messages].sort((a, b) => {
    const ta = a.datetime ? new Date(a.datetime).getTime() : 0
    const tb = b.datetime ? new Date(b.datetime).getTime() : 0
    return tb - ta
  })
  for (const msg of ordered) {
    const n = extractBlipRatingFromText(msg.message, maxRating)
    if (n != null) return n
  }
  return null
}

/** Rating em `wpp_ticket_list` costuma vir 0 / "0" quando não houve avaliação. */
export function isMissingTicketRating(rating: number | string | null | undefined): boolean {
  if (rating == null) return true
  const n = typeof rating === 'number' ? rating : Number(rating)
  return !Number.isFinite(n) || n === 0
}

export function resolveTicketRating(
  stored: number | string | null | undefined,
  messages: Array<Pick<WppTicketMessage, 'message' | 'datetime'>>,
  maxRating = 5,
): number | null {
  if (!isMissingTicketRating(stored)) {
    const n = typeof stored === 'number' ? stored : Number(stored)
    return Number.isFinite(n) ? n : null
  }
  return extractBlipRatingFromMessages(messages, maxRating)
}
